import { GetParams } from 'setup';
import { AnalyticsEvent } from '../hosting/events';

const STORAGE_KEY_QUEUE = 'analyticsQueue';
const MAX_CONCURRENT = 3;
const DELAY_MS = 10000;

/* ---------- persistence helpers ---------- */
function loadQueue(): AnalyticsEvent[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY_QUEUE) ?? '[]');
  } catch {
    return [];
  }
}

function saveQueue(queue: AnalyticsEvent[]) {
  localStorage.setItem(STORAGE_KEY_QUEUE, JSON.stringify(queue));
}

function clearQueue() {
  localStorage.setItem(
    STORAGE_KEY_QUEUE,
    JSON.stringify([] as AnalyticsEvent[])
  );
}

/* ---------- in-memory state ---------- */
let analyticsQueue: AnalyticsEvent[] = loadQueue();
let inFlight = 0;
let flushing = false;
let analyticsUrl = (window.getParams() as GetParams).analyticsUrl;
let analyticsPaused = false;

/* ---------- low-level sender ---------- */
async function postEvent(event: AnalyticsEvent): Promise<Response> {
  const controller = new AbortController();
  const signal = controller.signal;
  const timeoutId = setTimeout(
    () =>
      controller.abort(
        `Request to /track/event for event "${event.action}" aborted due to timeout.`
      ),
    DELAY_MS
  );

  window.addEventListener('beforeunload', () => {
    controller.abort('Tab closed, fetch aborted');
  }); // should I abort the fetch on tab closed?

  const res = await fetch(`${analyticsUrl}/track/event`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    keepalive: true,
    body: JSON.stringify(event),
    signal,
  });
  clearTimeout(timeoutId);

  const status = res.status;

  try {
    const responseData = await res.clone().json();

    const eventsPaused = responseData?.paused;

    if (responseData && typeof eventsPaused === 'boolean') {
      setPaused(eventsPaused);
    }

    if (res.ok) {
      console.log(
        `${status} - Analytics event "${event.action}" received by Analytics Forwarder.`
      );
    }

    if (status === 400) {
      throw new Error(`${status}: Bad request - ${responseData.error}.`);
    }

    if (status === 422) {
      throw new Error(
        `${status}: Unprocessable entity - ${responseData.error}.`
      );
    }
  } catch (e) {
    /* ignore — response has no body or isn’t JSON */
    console.error(
      e instanceof Error
        ? e.message
        : 'Unknown error sending event to analytics.',
      'Discarding event.'
    );
  }

  return res;
}

/* ---------- pause / resume helpers ---------- */
function setPaused(paused: boolean) {
  if (analyticsPaused === paused) return;
  analyticsPaused = paused;
  if (!analyticsPaused) {
    flushAnalyticsQueue(); // continue when un-paused
  } else {
    console.log('Analytics set to paused, clearing queue.');
    clearQueue();
  }
}

/** Optional manual resume API */
export function resume() {
  setPaused(false);
}

/* ---------- public enqueue ---------- */
export function enqueueAnalyticsEvent(event: AnalyticsEvent): Promise<void> {
  if (!analyticsPaused) {
    analyticsQueue.push(event);
    saveQueue(analyticsQueue);
  } else {
    console.warn(
      `Analytics events are paused on this module and will not be sent.`
    );
  }
  return flushAnalyticsQueue();
}

/* ---------- flush loop ---------- */
async function flushAnalyticsQueue(): Promise<void> {
  if (flushing || analyticsPaused) return;
  flushing = true;

  while (
    analyticsQueue.length > 0 &&
    navigator.onLine &&
    !analyticsPaused &&
    inFlight < MAX_CONCURRENT
  ) {
    const event = analyticsQueue.shift() as AnalyticsEvent;
    saveQueue(analyticsQueue);
    inFlight++;

    postEvent(event)
      .catch(error => {
        // network/server error → re-queue for retry
        console.error(
          error instanceof Error
            ? error.message
            : 'Analytics network or server error.',
          'Adding event back to analyticsQueue.'
        );
        analyticsQueue.unshift(event);
        saveQueue(analyticsQueue);
      })
      .finally(() => {
        inFlight--;
        flushAnalyticsQueue(); // attempt to launch again immediately
      });
  }

  flushing = false;
}

/* ---------- network listeners ---------- */
window.addEventListener('online', () => {
  flushAnalyticsQueue();
});
